import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertTriangle, Layers, ListChecks, Plus, RefreshCw, Search, UserPlus, X } from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { CursorPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import { BatchesTab } from './BatchesTab'
import { IntakeDialog } from './IntakeDialog'
import { TransferCharts } from './TransferCharts'
import {
  INTAKE_CHANNEL_LABEL, REGISTRATION_REASON_CODE, STATUS_TONE, TRANSFER_REASON_LABEL,
  TRANSFER_STATUS_LABEL, listTransfers, searchTransfersByNationalId, transferStatistics,
} from './api'

/**
 * The transfer queue.
 *
 * Two views, because two different people use this screen: a caseworker raising
 * and chasing individual requests, and someone working a dissolved station as a
 * batch. Filters live in the URL so a queue can be sent to a colleague — "these
 * four are breaching SLA" is a link, not a screenshot.
 */

const VIEWS = [
  { id: 'queue', label: 'Requests', icon: ListChecks },
  { id: 'batches', label: 'Batches', icon: Layers },
] as const

/**
 * Is this search term a national ID rather than a name or a reference?
 *
 * Kenyan national IDs are 7–9 digits. A reference is `TR-2026-000123` / `RG-2026-000045`,
 * so a digits-only term could also be the numeric tail someone typed — but the tail is 6
 * digits and an ID is at least 7, which separates them cleanly. When it IS an ID, the
 * search goes through POST so the number never reaches a URL, a log or a history entry.
 */
export const looksLikeNationalId = (term: string) => /^\d{7,9}$/.test(term.trim())

/** The statuses worth filtering to, in lifecycle order rather than alphabetically. */
const STATUS_FILTERS = [
  'DRAFT', 'SUBMITTED', 'VERIFYING', 'INELIGIBLE', 'UNDER_REVIEW', 'APPROVED',
  'LODGED', 'CONFIRMED', 'RECORDED', 'UNDER_APPEAL', 'REJECTED', 'FAILED_AT_IEBC',
  'WITHDRAWN', 'EXPIRED', 'REVERSED',
]

export function TransfersPage() {
  const canRead = useCan('relocation:read')
  const canCreate = useCan('relocation:create')
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const rawView = params.get('view')
  const view = VIEWS.some((v) => v.id === rawView) ? rawView! : 'queue'
  const status = params.get('status') ?? ''
  const reason = params.get('reason') ?? ''
  const channel = params.get('channel') ?? ''
  const kind = params.get('kind') ?? ''
  const q = params.get('q') ?? ''
  const slaBreached = params.get('slaBreached') === 'true'

  // What was typed, before it is committed. A box that filtered on every keystroke
  // would fire one lookup per digit.
  const [draftQ, setDraftQ] = useState(q)
  useEffect(() => { setDraftQ(q) }, [q])

  /**
   * A national ID is held **here and nowhere else**.
   *
   * It never reaches `setSearchParams`, so it never reaches the address bar, the SPA
   * history entry, a copied link or the `Referer` of anything this document fetches.
   * The URL carries `idsearch=1` — a flag saying an ID search is running — and the
   * digits stay in memory. The consequence is deliberate: reload the page and the
   * search is gone, because a number that survived a reload would have had to be stored
   * somewhere, and there is nowhere safe to store it.
   */
  const [idTerm, setIdTerm] = useState('')
  const idFlag = params.get('idsearch') === '1'
  const idSearch = idTerm !== ''

  const runSearch = (raw: string) => {
    const term = raw.trim()
    if (looksLikeNationalId(term)) {
      setIdTerm(term)
      patch({ q: null, idsearch: '1' })
    } else {
      setIdTerm('')
      patch({ q: term || null, idsearch: null })
    }
  }

  const clearSearch = () => { setDraftQ(''); setIdTerm(''); patch({ q: null, idsearch: null }) }

  /**
   * Charts are worth a whole screen on a desktop and are in the way on a phone, where
   * the first thing a mobilizer needs is the row they are looking for. So the default
   * follows the viewport — and an explicit `charts=` in the URL always wins, because a
   * link someone sent should look the same on both their screens.
   */
  const chartsParam = params.get('charts')
  const showCharts = chartsParam === null
    ? typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
    : chartsParam !== '0'

  const [cursors, setCursors] = useState<string[]>([])
  const cursor = cursors[cursors.length - 1]

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
    setCursors([])          // a filter change invalidates the page stack
  }

  const filters = {
    status: status || undefined,
    reason: reason || undefined,
    channel: channel || undefined,
    kind: kind || undefined,
    slaBreached: slaBreached ? true : undefined,
  }

  const list = useLoad(
    () => (!canRead ? Promise.resolve(null)
      : idSearch ? searchTransfersByNationalId(idTerm, filters, cursor)
        : listTransfers({ ...filters, q: q || undefined }, cursor)),
    [canRead, status, reason, channel, kind, q, idTerm, slaBreached, cursor])

  /**
   * The counters and the charts carry the SAME filters as the table, including the name
   * search — statistics that ignore a filter describe a different set of rows than the
   * list beneath them, which is worse than showing nothing: it is a number that looks
   * answerable and is not.
   *
   * An ID search is the exception, and it is handled by not showing them at all rather
   * than by filtering them: the term cannot go in a GET, and "one person's requests" is
   * not a thing anyone charts.
   */
  const stats = useLoad(
    () => (canRead && !idSearch
      ? transferStatistics({ ...filters, q: q || undefined })
      : Promise.resolve(null)),
    [canRead, status, reason, channel, kind, q, idSearch, slaBreached])

  const [creating, setCreating] = useState(false)

  if (!canRead) return <Forbidden what="transfers" />

  const s = stats.data
  const rows = list.data?.content ?? []

  // Open work is what a queue is for — everything not yet at rest.
  const open = s
    ? ['DRAFT', 'SUBMITTED', 'VERIFYING', 'UNDER_REVIEW', 'APPROVED', 'LODGED', 'UNDER_APPEAL']
        .reduce((n, k) => n + (s.byStatus[k] ?? 0), 0)
    : 0

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Voter transfers"
        description="Requests to correct where a voter is registered. The IEBC performs transfers; this records and evidences them."
        right={
          <div className="flex gap-1.5">
            <button
              type="button"
              className="erp-btn"
              aria-label="Refresh"
              onClick={() => { void list.reload(); void stats.reload() }}
            >
              <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
            </button>
            {canCreate && (
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                aria-label="Raise a request"
                onClick={() => setCreating(true)}
              >
                <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Raise a request</span>
              </button>
            )}
          </div>
        }
      />

      {!idSearch && (
      <KpiStrip>
        <MetricCard label="Open" value={fmtNumber(open)} accent sub="not yet at rest" />
        <MetricCard label="Recorded" value={fmtNumber(s?.byStatus.RECORDED ?? 0)} sub="on our register" />
        <MetricCard label="Awaiting IEBC" value={fmtNumber(s?.byStatus.LODGED ?? 0)} sub="lodged, not confirmed" />
        <MetricCard
          label="SLA breached"
          value={fmtNumber(s?.slaBreached ?? 0)}
          sub={s?.slaBreached ? 'someone is waiting' : 'none'}
        />
      </KpiStrip>
      )}

      <div className="border-b border-border flex items-center justify-between gap-2">
        <div className="flex gap-1 overflow-x-auto">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => patch({ view: v.id })}
              className={cn(
                'px-3 py-2 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap',
                view === v.id
                  ? 'border-chocolate text-chocolate font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              <v.icon className="h-3.5 w-3.5" /> {v.label}
            </button>
          ))}
        </div>
        {view === 'queue' && (
          <button
            type="button"
            className="text-erp-xs text-muted-foreground hover:text-foreground whitespace-nowrap px-2 axis-keep-narrow"
            aria-pressed={showCharts}
            onClick={() => patch({ charts: showCharts ? '0' : '1' })}
          >
            {showCharts ? 'Hide charts' : 'Show charts'}
          </button>
        )}
      </div>

      {view === 'batches' ? (
        <BatchesTab />
      ) : (
        <>
          {showCharts && !idSearch && <TransferCharts stats={s ?? null} />}

          <Panel dense>
            <div className="flex flex-wrap items-center gap-2">
              <form
                className="flex items-center gap-1.5"
                onSubmit={(e) => { e.preventDefault(); runSearch(draftQ) }}
              >
                <div className="flex items-center gap-2 px-3 h-9 border border-input rounded-md bg-background">
                  <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <input
                    value={draftQ}
                    onChange={(e) => setDraftQ(e.target.value)}
                    placeholder="Name, reference or ID number"
                    aria-label="Search by name or ID number"
                    className="bg-transparent outline-none text-erp-sm w-52 max-w-[45vw] min-w-0"
                  />
                  {(q || idSearch) && (
                    <button type="button" aria-label="Clear search" className="shrink-0"
                            onClick={clearSearch}>
                      <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </div>
                <button type="submit" className="erp-btn">Search</button>
              </form>

              <select
                className="erp-input"
                value={kind}
                onChange={(e) => patch({ kind: e.target.value })}
                aria-label="Kind"
              >
                <option value="">Transfers and registrations</option>
                <option value="TRANSFER">Transfers only</option>
                <option value="REGISTRATION">Registrations only</option>
              </select>

              <select
                className="erp-input"
                value={status}
                onChange={(e) => patch({ status: e.target.value })}
                aria-label="Status"
              >
                <option value="">Status: any</option>
                {STATUS_FILTERS.map((c) => (
                  <option key={c} value={c}>{TRANSFER_STATUS_LABEL[c] ?? humanise(c)}</option>
                ))}
              </select>

              <select
                className="erp-input"
                value={reason}
                onChange={(e) => patch({ reason: e.target.value })}
                aria-label="Reason"
              >
                <option value="">Reason: any</option>
                {Object.entries(TRANSFER_REASON_LABEL).map(([c, l]) => (
                  <option key={c} value={c}>{l}</option>
                ))}
              </select>

              <select
                className="erp-input"
                value={channel}
                onChange={(e) => patch({ channel: e.target.value })}
                aria-label="Intake channel"
              >
                <option value="">Intake: any</option>
                {Object.entries(INTAKE_CHANNEL_LABEL).map(([c, l]) => (
                  <option key={c} value={c}>{l}</option>
                ))}
              </select>

              <button
                type="button"
                aria-pressed={Boolean(slaBreached)}
                onClick={() => patch({ slaBreached: slaBreached ? null : 'true' })}
                className={cn(
                  'erp-btn',
                  slaBreached && 'border-red-400 text-red-700 dark:text-red-400',
                )}
              >
                <AlertTriangle className="h-3.5 w-3.5" /> SLA breached only
              </button>
            </div>
          </Panel>

          {idSearch && (
            <p className="text-erp-xs text-muted-foreground px-1">
              Searching by ID number. The number is sent in the request body and is not in the
              link — so this search does not survive a reload, deliberately.
            </p>
          )}
          {idFlag && !idSearch && (
            <p className="text-erp-xs text-amber-700 dark:text-amber-400 px-1">
              This link had an ID search on it. The number itself was never in the link, so
              type it again to run the search.
            </p>
          )}

          <ErrorStrip message={list.error} />

          <Panel>
            {list.loading && rows.length === 0 ? (
              <LoadingBlock label="Loading requests…" />
            ) : rows.length === 0 ? (
              <EmptyState
                title="Nothing here"
                hint={
                  status || reason || channel || kind || q || idSearch || slaBreached
                    ? 'Nothing matches. Clear a filter, or check the ID number.'
                    : 'No transfer request has been raised in your scope yet.'
                }
              />
            ) : (
              <>
                <ErpTable
                  head={
                    <>
                      <Th>Person</Th>
                      <Th>Reference</Th>
                      <Th>Status</Th>
                      <Th className="axis-hide-md">Reason</Th>
                      <Th className="axis-hide-md">Intake</Th>
                      <Th className="axis-hide-lg">To</Th>
                      <Th className="axis-hide-lg">SLA</Th>
                      <Th className="axis-hide-sm">Raised</Th>
                    </>
                  }
                >
                    {rows.map((t) => (
                      <tr
                        key={t.id}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open ${t.reference}`}
                        className="cursor-pointer hover:bg-muted/40"
                        onClick={() => navigate(`/transfers/${t.id}`)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            navigate(`/transfers/${t.id}`)
                          }
                        }}
                      >
                        <Td>
                          <div className="flex items-start gap-1.5 min-w-0">
                            {t.kind === 'REGISTRATION' && (
                              <UserPlus
                                className="h-3.5 w-3.5 mt-0.5 shrink-0 text-chocolate"
                                aria-label="Registration"
                              />
                            )}
                            <div className="min-w-0">
                              {/* A queue of uuids is a queue nobody can work. When the
                                  server cannot resolve the person — erased, merged,
                                  out of scope — the row still appears, because a
                                  request that vanishes with its subject is an audit
                                  hole. It says so instead of showing an id. */}
                              <div className="truncate">
                                {t.voterName ?? (
                                  <span className="text-muted-foreground italic">
                                    name not available
                                  </span>
                                )}
                              </div>
                              {(t.nationalIdMasked || t.voterNumber) && (
                                <div className="text-erp-xs text-muted-foreground font-mono truncate">
                                  {t.nationalIdMasked && `ID ${t.nationalIdMasked}`}
                                  {t.nationalIdMasked && t.voterNumber && ' · '}
                                  {t.voterNumber}
                                </div>
                              )}
                            </div>
                          </div>
                        </Td>
                        <Td className="font-mono text-erp-xs">{t.reference}</Td>
                        <Td>
                          <span className="inline-flex items-center gap-1.5">
                            <StatusDot status={t.status} />
                            {TRANSFER_STATUS_LABEL[t.status] ?? humanise(t.status)}
                          </span>
                        </Td>
                        <Td className="axis-hide-md">
                          {t.reason === REGISTRATION_REASON_CODE
                            ? 'Not yet registered'
                            : TRANSFER_REASON_LABEL[t.reason] ?? humanise(t.reason)}
                        </Td>
                        <Td className="axis-hide-md">
                          {INTAKE_CHANNEL_LABEL[t.intakeChannel] ?? humanise(t.intakeChannel)}
                        </Td>
                        <Td className="axis-hide-lg truncate max-w-[14rem]">
                          {/* The station's NAME, which the server now resolves. The
                              ltree path is the fallback and lives in the title, because
                              a column of `047_WES_KANGEMI` is a column nobody reads. */}
                          {t.toStationName ?? (
                            <span className="font-mono text-erp-xs" title={t.toUnitPath ?? ''}>
                              {(t.toUnitPath ?? '').split('.').pop() ?? '—'}
                            </span>
                          )}
                        </Td>
                        <Td className="axis-hide-lg">
                          {t.slaBreached
                            ? <span className="text-red-700 dark:text-red-400">Breached</span>
                            : t.slaDueAt ? fmtDateTime(t.slaDueAt) : '—'}
                        </Td>
                        <Td className="axis-hide-sm">{fmtDateTime(t.createdAt)}</Td>
                      </tr>
                    ))}
                </ErpTable>

                <CursorPager
                  page={cursors.length}
                  count={rows.length}
                  hasMore={list.data?.hasMore ?? false}
                  canGoBack={cursors.length > 0}
                  loading={list.loading}
                  onNext={() => {
                    const next = list.data?.nextCursor
                    if (next) setCursors((c) => [...c, next])
                  }}
                  onBack={() => setCursors((c) => c.slice(0, -1))}
                  unit="requests"
                />
              </>
            )}
          </Panel>
        </>
      )}

      {creating && (
        <IntakeDialog
          onClose={() => setCreating(false)}
          onCreated={(id: string) => { setCreating(false); navigate(`/transfers/${id}`) }}
        />
      )}
    </div>
  )
}

function StatusDot({ status }: { status: string }) {
  const tone = STATUS_TONE[status]
  return (
    <span
      aria-hidden
      className={cn(
        'h-1.5 w-1.5 rounded-full shrink-0',
        tone === 'good' && 'bg-emerald-600',
        tone === 'bad' && 'bg-red-600',
        tone === 'warn' && 'bg-amber-500',
        (tone === 'open' || !tone) && 'bg-chocolate/60',
      )}
    />
  )
}
